import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiIsLoaded } from "@vis.gl/react-google-maps";
import { bbox } from "@turf/bbox";
import { center } from "@turf/center";

import { formatAddress } from "helpers/address";
import { proximityBandFor } from "helpers/proximityRings";
import { useDistanceLimits } from "hooks/useDistanceLimits";
import AddressService from "services/Address";
import CaseEventService from "services/CaseEvent";

const fetchAddresses = async ({ queryKey }) => {
  const [, modelType, modelId] = queryKey;
  const resp = await AddressService.getAddressesForModel({
    model_id: modelId,
    model_type: modelType,
    map: true,
  });
  return resp.addresses || [];
};

const fetchNearbyEvents = async ({ queryKey }) => {
  const [, modelType, modelId, locationType] = queryKey;
  // TODO: Include IP events?
  const resp = await CaseEventService.getNearbyEvents({
    model_id: modelId,
    model_type: modelType,
    location_type: locationType,
  });
  return resp.data || [];
};

const parseAddressesToMarkers = (addresses = []) =>
  addresses
    .filter(address => address.latitude && address.longitude)
    .map(address => {
      return {
        position: {
          lat: address.latitude,
          lng: address.longitude,
        },
        color: "blue",
        title: formatAddress(address),
        type: "address",
      };
    });

// Rings are drawn around every address pin, so the band an event reads as is
// the one set by the address it sits closest to.
const nearestDistance = distances =>
  distances?.length
    ? Math.min(...distances.map(location => location.distance))
    : undefined;

const parseEventsToMarkers = (events = [], limits) =>
  events.map(event => {
    return {
      position: {
        lat: event.lat,
        lng: event.lng,
      },
      title: `${event.lat}, ${event.lng}`,
      color: proximityBandFor(nearestDistance(event.distances), limits),
      type: "event",
      count: event.event_count,
      data: event,
    };
  });

const parseLocationsToMarkers = (locations = []) =>
  locations.map(locationData => {
    return {
      position: {
        lat: locationData.lat,
        lng: locationData.lng,
      },
      title: `${locationData.lat}, ${locationData.lng}`,
      color: "grey",
      type: "location",
      data: locationData.data,
    };
  });

const emptyMarkers = {
  data: [],
  bounds: null,
  center: null,
  geoJson: { type: "FeatureCollection", features: [] },
};

export function useAppMapMarkers({
  modelId,
  modelType,
  locationType,
  hasAddresses,
  locations = [],
}) {
  const { data: addresses, isFetching: isLoadingAddresses } = useQuery({
    queryKey: ["addresses", modelType, modelId],
    queryFn: fetchAddresses,
    enabled: hasAddresses && !!modelId && !!modelType,
    initialData: [],
  });

  const { data: nearbyEvents, isFetching: isLoadingEvents } = useQuery({
    queryKey: ["nearbyEvents", modelType, modelId, locationType],
    queryFn: fetchNearbyEvents,
    enabled: !!modelId && !!modelType && modelType !== "Case Event",
    initialData: [],
  });

  // isLoading rather than the isFetching its siblings use: a refetch after an
  // admin edits the limits should reband the pins in place, not blank the map.
  const { data: distanceLimits, isLoading: isLoadingDistanceLimits } =
    useDistanceLimits();

  const isApiLoaded = useApiIsLoaded();

  // The bands are read from the limits, so drawing before they land would paint
  // every event pin grey and then refit the map when the real colours arrive.
  const isLoading =
    !isApiLoaded ||
    isLoadingAddresses ||
    isLoadingEvents ||
    isLoadingDistanceLimits;

  const markers = useMemo(() => {
    if (isLoading) {
      return emptyMarkers;
    }

    const addressMarkers = parseAddressesToMarkers(addresses);
    const eventMarkers = parseEventsToMarkers(nearbyEvents, distanceLimits);
    const locationMarkers = parseLocationsToMarkers(locations);
    const allMarkers = [
      ...addressMarkers,
      ...eventMarkers,
      ...locationMarkers,
    ].filter(
      marker =>
        Number.isFinite(marker.position.lat) &&
        Number.isFinite(marker.position.lng)
    );

    if (allMarkers.length === 0) {
      return emptyMarkers;
    }

    // The marker is nested under its own key so its fields never collide with
    // the cluster metadata (cluster, point_count, ...) supercluster writes onto
    // a feature's properties.
    const collection = {
      type: "FeatureCollection",
      features: allMarkers.map((marker, index) => ({
        type: "Feature",
        id: index,
        geometry: {
          type: "Point",
          coordinates: [marker.position.lng, marker.position.lat],
        },
        properties: { marker },
      })),
    };
    const bounds = bbox(collection);
    const mapCenter = center(collection);

    return {
      geoJson: collection,
      data: allMarkers,
      bounds: new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds[1], bounds[0]),
        new window.google.maps.LatLng(bounds[3], bounds[2])
      ),
      center: new window.google.maps.LatLng(
        mapCenter.geometry.coordinates[1],
        mapCenter.geometry.coordinates[0]
      ),
    };
  }, [addresses, nearbyEvents, locations, distanceLimits, isLoading]);

  return {
    markers,
    isLoading,
  };
}
